import type { AuditCenterRow, AuditCenterSource, AuditSearchParams, PatientEventKind } from '@/types/audit'
import { auditActorLabel, renderAuditValue } from '@/lib/auditTrail'
import { PERMISSIONS as P } from '@/lib/permissions'

/**
 * Pure rules for the Audit Trail Center (z8nrz7dq6q, spec 2026-09-22): which
 * sources exist, how a search is written into a URL and back, and what an
 * export contains. Kept out of the components so they are unit-tested
 * (vitest here runs `.ts` only).
 */

/** The spec's source chips, in the spec's order. */
export const AUDIT_CENTER_SOURCES: { value: AuditCenterSource; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'storage', label: 'Storage' },
  { value: 'product', label: 'Product' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'procedure', label: 'Procedures' },
  { value: 'field', label: 'Field' },
  // Audit trail Label and Patient (2026-10-10): one patient's whole history,
  // and every label printed.
  { value: 'patient', label: 'Patient' },
  { value: 'labels', label: 'Labels' },
  // Every action a phone did offline and sent on reconnect, including the
  // ones that were not applied (z8nrz7fh5c).
  { value: 'offline', label: 'Offline sync' },
]

const SOURCE_LABEL = new Map(AUDIT_CENTER_SOURCES.map((s) => [s.value, s.label]))

/**
 * The trail shown when none is picked. One trail is always shown, one at a
 * time (the client, 2026-09-29): each trail is read as its own screen shows
 * it, so there is no mixed "every trail" view any more.
 */
export const DEFAULT_AUDIT_SOURCE: AuditCenterSource = AUDIT_CENTER_SOURCES[0].value

/**
 * The key that opens each trail on its own; `audit.read` opens them all. The
 * server holds the search to the same trails, so a chip shown here never 403s.
 */
export const AUDIT_TRAIL_PERMISSION: Record<AuditCenterSource, string> = {
  user: P.auditUserRead,
  storage: P.auditStorageRead,
  product: P.auditProductRead,
  inventory: P.auditInventoryRead,
  equipment: P.auditEquipmentRead,
  procedure: P.auditProcedureRead,
  field: P.auditFieldRead,
  patient: P.auditPatientRead,
  labels: P.auditLabelsRead,
  offline: P.auditOfflineRead,
}

/** Any one of these opens the Audit Trail Center. */
export const AUDIT_CENTER_READ_PERMISSIONS: readonly string[] = [P.auditRead, ...Object.values(AUDIT_TRAIL_PERMISSION)]

/** The trails whose chip this user sees, in chip order. `canAny` is usePermission's. */
export function readableTrails(canAny: (keys: readonly string[]) => boolean): AuditCenterSource[] {
  return AUDIT_CENTER_SOURCES.map((s) => s.value).filter((s) => canAny([P.auditRead, AUDIT_TRAIL_PERMISSION[s]]))
}

/**
 * The search on a trail the user may read: a URL naming another trail (shared
 * by someone who can read it, or the User default) opens on their first one.
 */
export function onReadableTrail(c: AuditCriteria, readable: readonly AuditCenterSource[]): AuditCriteria {
  return readable.length && !readable.includes(auditSource(c)) ? { ...c, sources: [readable[0]] } : c
}

/**
 * User is everything people did — signed in, opened a page, created a
 * procedure, removed a field, published a template, moved stock — read from
 * every trail where it is already recorded, presented under the user and never
 * copied (the client, 2026-09-29). So it searches every trail; naming a person
 * narrows it to them.
 */
export function userActivity(c: Pick<AuditCriteria, 'sources'>): boolean {
  return auditSource(c) === 'user'
}

/**
 * The trails User reads. Patient, Labels and Offline sync are left out: each
 * is a view over rows another trail already holds (a patient's forms are
 * Procedures rows, a label print is the Product trail's "label issued", an
 * applied offline action is an Equipment row), so reading them too would list
 * the same action twice.
 */
const USER_ACTIVITY_SOURCES: AuditCenterSource[] = AUDIT_CENTER_SOURCES.map((s) => s.value).filter(
  (s) => s !== 'patient' && s !== 'labels' && s !== 'offline',
)

/** The Patient trail: one patient's history, so it shows nothing until a patient is named. */
export function patientHistory(c: Pick<AuditCriteria, 'sources'>): boolean {
  return auditSource(c) === 'patient'
}

/** Whether a search can run: the Patient trail needs its patient. */
export function canSearch(c: AuditCriteria): boolean {
  return !patientHistory(c) || searchValue(c.patient) !== ''
}

/** What a Patient row can be, in the order the filter lists them. */
export const PATIENT_EVENT_KINDS: { value: PatientEventKind; label: string }[] = [
  { value: 'form', label: 'Form' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'product', label: 'Product' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'patient', label: 'Patient' },
]

const PATIENT_KIND_LABEL = new Map(PATIENT_EVENT_KINDS.map((k) => [k.value as string, k.label]))

export function patientKindLabel(kind: string | null | undefined): string | null {
  return kind ? (PATIENT_KIND_LABEL.get(kind) ?? null) : null
}

/** The one trail a search shows. */
export function auditSource(c: Pick<AuditCriteria, 'sources'>): AuditCenterSource {
  return c.sources[0] ?? DEFAULT_AUDIT_SOURCE
}

export function auditTrailLabel(source: AuditCenterSource): string {
  return SOURCE_LABEL.get(source) ?? source
}

/** The spec's "Search by" list, with the words and hints the form shows. */
export const ADVANCED_FIELDS = [
  { key: 'patient', label: 'Patient', placeholder: 'MRN' },
  { key: 'product', label: 'Product', placeholder: 'DIN or product code' },
  { key: 'workOrder', label: 'Work order', placeholder: 'e.g. WO-26-0043' },
  { key: 'mfgLot', label: 'MFG / Lot ID', placeholder: 'MFG number, kit or lot number' },
  { key: 'inventoryItem', label: 'Inventory item', placeholder: 'Kit, consumable or reagent' },
  { key: 'equipment', label: 'Equipment', placeholder: 'Equipment ID or model' },
  { key: 'field', label: 'Field name', placeholder: 'e.g. CD34' },
  { key: 'changedBy', label: 'Changed by', placeholder: 'Name or email' },
] as const

export type AdvancedKey = (typeof ADVANCED_FIELDS)[number]['key']

/**
 * The filters each trail can answer — the backend drops a trail's table when a
 * filter it cannot answer is set, so offering one there only empties the list.
 * User reads every trail, so every filter applies; its person is the User box.
 */
const TRAIL_FILTERS: Record<AuditCenterSource, readonly AdvancedKey[]> = {
  // The patient is the trail's own box, as the person is User's.
  user: ['patient', 'product', 'workOrder', 'mfgLot', 'inventoryItem', 'equipment', 'field'],
  storage: ['equipment', 'mfgLot', 'inventoryItem', 'changedBy'],
  product: ['patient', 'product', 'workOrder', 'mfgLot', 'equipment', 'field', 'changedBy'],
  inventory: ['inventoryItem', 'mfgLot', 'equipment', 'workOrder', 'changedBy'],
  equipment: ['equipment', 'workOrder', 'mfgLot', 'changedBy'],
  procedure: ['patient', 'workOrder', 'mfgLot', 'product', 'inventoryItem', 'equipment', 'field', 'changedBy'],
  field: ['patient', 'field', 'workOrder', 'mfgLot', 'changedBy'],
  patient: ['changedBy'],
  labels: ['product', 'changedBy'],
  offline: ['equipment', 'changedBy'],
}

/** The advanced filters shown for a trail, in the order that trail is searched by. */
export function advancedFieldsFor(source: AuditCenterSource): (typeof ADVANCED_FIELDS)[number][] {
  return TRAIL_FILTERS[source].map((key) => ADVANCED_FIELDS.find((f) => f.key === key)!)
}

/** What each trail's general search finds, as the prompt in its box. */
export const SEARCH_PLACEHOLDER: Record<AuditCenterSource, string> = {
  user: 'Page, record, location, material, value or action',
  storage: 'Location, material, lot number or DIN',
  product: 'DIN, product code or MRN / KIT / WO',
  inventory: 'Material, lot number or location',
  equipment: 'Equipment ID, model or serial number',
  procedure: 'MRN, work order, MFG number, procedure, field or value',
  field: 'Field, value, MRN or procedure',
  patient: 'Form, field, value, DIN or action',
  labels: 'Label #, DIN, product code, label type or signer',
  offline: 'Equipment, reason or device',
}

/**
 * A search value as typed or pasted: trimmed, and without the invisible break
 * characters the tables put after each "/" of a location so it can wrap — a
 * location copied from a row would otherwise match nothing.
 */
export function searchValue(v: string): string {
  return v.replace(/[\u200b-\u200d\ufeff]/g, '').trim()
}

/**
 * A search as the form holds it: strings, '' meaning "not set". Dates are
 * calendar days (`YYYY-MM-DD`), both ends inclusive — a reviewer asking for
 * "the 3rd to the 5th" means all of the 5th.
 */
export type AuditCriteria = {
  sources: AuditCenterSource[]
  q: string
  fromDay: string
  toDay: string
  /** Patient trail: the one kind of event to show, '' for every kind. */
  kind: PatientEventKind | ''
} & Record<AdvancedKey, string>

export const EMPTY_CRITERIA: AuditCriteria = {
  sources: [],
  q: '',
  patient: '',
  product: '',
  workOrder: '',
  mfgLot: '',
  inventoryItem: '',
  equipment: '',
  field: '',
  changedBy: '',
  fromDay: '',
  toDay: '',
  kind: '',
}

const ADVANCED_KEYS = ADVANCED_FIELDS.map((f) => f.key)
const SOURCE_VALUES = new Set<string>(AUDIT_CENTER_SOURCES.map((s) => s.value))
const KIND_VALUES = new Set<string>(PATIENT_EVENT_KINDS.map((k) => k.value))

/** Local midnight of a `YYYY-MM-DD` day, as an ISO instant; null for a bad day. */
export function dayStartIso(day: string, addDays = 0): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + addDays)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** The filters of a search its trail can answer, each as sent; a filter left set on another trail is not. */
function answerable(c: AuditCriteria): [AdvancedKey, string][] {
  const keys: readonly AdvancedKey[] = userActivity(c)
    ? [...TRAIL_FILTERS.user, 'changedBy']
    : patientHistory(c)
      ? ['patient', ...TRAIL_FILTERS.patient]
      : TRAIL_FILTERS[auditSource(c)]
  return keys.map((key) => [key, searchValue(c[key])] as [AdvancedKey, string]).filter(([, v]) => v)
}

/** Criteria → the API's params. The "to" day is inclusive, so it ends at the NEXT midnight. */
export function criteriaToParams(c: AuditCriteria): AuditSearchParams {
  const params: AuditSearchParams = {
    sources: userActivity(c) ? USER_ACTIVITY_SOURCES : [auditSource(c)],
  }
  if (searchValue(c.q)) params.q = searchValue(c.q)
  for (const [key, v] of answerable(c)) params[key] = v
  if (patientHistory(c) && c.kind) params.kind = c.kind
  const from = c.fromDay ? dayStartIso(c.fromDay) : null
  const to = c.toDay ? dayStartIso(c.toDay, 1) : null
  if (from) params.from = from
  if (to) params.to = to
  return params
}

/** The API's query string. Sources travel comma-separated. */
export function auditSearchQuery(params: AuditSearchParams): URLSearchParams {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (key === 'sources') {
      if ((value as string[]).length) qs.set('sources', (value as string[]).join(','))
    } else {
      qs.set(key, String(value))
    }
  }
  return qs
}

/**
 * Criteria → the Center page's own URL, so a search can be bookmarked, shared
 * with QA, and survives a reload. Only what is set is written.
 */
export function criteriaToUrl(c: AuditCriteria): URLSearchParams {
  const sp = new URLSearchParams()
  if (c.sources.length) sp.set('sources', c.sources.join(','))
  if (c.q.trim()) sp.set('q', c.q.trim())
  for (const key of ADVANCED_KEYS) if (c[key].trim()) sp.set(key, c[key].trim())
  if (c.fromDay) sp.set('from', c.fromDay)
  if (c.toDay) sp.set('to', c.toDay)
  if (c.kind) sp.set('kind', c.kind)
  return sp
}

/** The Center page's URL → criteria. Unknown sources and malformed days are ignored. */
export function criteriaFromUrl(sp: URLSearchParams): AuditCriteria {
  const day = (v: string | null) => (v && dayStartIso(v) ? v : '')
  const c: AuditCriteria = {
    ...EMPTY_CRITERIA,
    sources: (sp.get('sources') ?? '')
      .split(',')
      .filter((s): s is AuditCenterSource => SOURCE_VALUES.has(s))
      // One trail at a time: a link from before that named several opens the first.
      .slice(0, 1),
    q: sp.get('q') ?? '',
    fromDay: day(sp.get('from')),
    toDay: day(sp.get('to')),
    kind: KIND_VALUES.has(sp.get('kind') ?? '') ? (sp.get('kind') as PatientEventKind) : '',
  }
  for (const key of ADVANCED_KEYS) c[key] = sp.get(key) ?? ''
  return c
}

/** Whether any advanced filter or date is set — the "Advanced" panel opens when so. */
export function hasAdvancedCriteria(c: AuditCriteria): boolean {
  return advancedFieldsFor(auditSource(c)).some((f) => searchValue(c[f.key]) !== '') || !!c.fromDay || !!c.toDay
}

// ─── Export ──────────────────────────────────────────────────────────────────

export const EXPORT_COLUMNS = [
  'Date & time',
  'Audit trail',
  'Record',
  'Changed by',
  'Type',
  'Item changed',
  'Old value',
  'New value',
  'Reason',
  'Description',
  'MRN / KIT / WO',
  'DIN',
  'Product code',
  'Storage node',
  'Change type',
  'Transaction',
] as const

/** One result row as the spreadsheet row, in EXPORT_COLUMNS order. */
export function exportRow(r: AuditCenterRow): string[] {
  const value = (v: unknown) => {
    const text = renderAuditValue(v)
    return text === '—' ? '' : text
  }
  return [
    new Date(r.changedAt).toISOString(),
    auditTrailLabel(r.trail),
    r.record?.label ?? '',
    auditActorLabel(r),
    r.transactionType,
    r.itemChanged ?? '',
    value(r.oldValue),
    value(r.newValue),
    r.reason ?? '',
    r.description ?? '',
    r.subjectKey ? `${r.subjectKeyType ?? ''} ${r.subjectKey}`.trim() : '',
    r.din ?? '',
    r.productCode ?? '',
    r.storageNodeLabel ?? '',
    r.changeType,
    r.transactionId,
  ]
}

/**
 * The "Search criteria" sheet: what was searched, so an export read later —
 * by an inspector, in an investigation file — says what it is a result of
 * ("Maintain Search Context" in the spec).
 */
export function criteriaLines(
  c: AuditCriteria,
  meta: { tenant: string; exportedBy: string; exportedAt: Date; total: number; exported: number },
): [string, string][] {
  const lines: [string, string][] = [
    ['Tenant', meta.tenant],
    ['Exported by', meta.exportedBy],
    ['Exported at', meta.exportedAt.toISOString()],
    ['Audit trail', auditTrailLabel(auditSource(c))],
    ['General search', searchValue(c.q) || '—'],
  ]
  for (const [key, v] of answerable(c)) lines.push([ADVANCED_FIELDS.find((f) => f.key === key)!.label, v])
  if (patientHistory(c)) lines.push(['Kind of event', patientKindLabel(c.kind) ?? 'All'])
  if (c.fromDay || c.toDay) lines.push(['Date range', `${c.fromDay || '…'} to ${c.toDay || '…'} (inclusive)`])
  lines.push(['Rows matched', String(meta.total)])
  lines.push(['Rows exported', String(meta.exported)])
  return lines
}

/** "audit-trail-2026-09-28.xlsx". */
export function exportFileName(now: Date, scope?: string): string {
  const day = now.toISOString().slice(0, 10)
  return `audit-trail${scope ? `-${scope}` : ''}-${day}.xlsx`
}
